import { Suspense, lazy, useState } from 'react'
import { Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ExecutionRecord } from '@/services/api/retrostock-api'
import { useTradeRunState } from '@/features/backtest/context/trade-run-context'
import {
  CASH,
  COUNT,
  PERCENT,
  USD,
  formatSummaryNumber,
  relativeTime,
} from '@/features/backtest/components/run/run-formatters'
import { summaryForRun } from '@/features/backtest/utils/summary-for-run'
import { runProgress } from '@/features/backtest/components/run/run-progress'

// Lazy, not for size: the dialog's summary reads the node schemas, and the
// schemas mount this section, so a static import is an import cycle.
const RunConfirmDialog = lazy(() =>
  import('@/features/backtest/components/editor/run-confirm-dialog').then((m) => ({
    default: m.RunConfirmDialog,
  })),
)

const isLive = (status: string | undefined) => status === 'RUNNING' || status === 'PENDING'

/**
 * Run, and why not: the button that asks first ("Run this backtest?",
 * components/editor/run-confirm-dialog.tsx), then saves the strategy, launches
 * it on the connected universe and opens the Runs tab on the new run
 * (hooks/use-trade-run.ts), with one line under it: the reason Run is
 * unavailable, or (where the run settings are not on screen) how the run will
 * be sized. Back on the node 2026-10-28, after a day in the header (Chris).
 */
function TradeRunButton({ settingsNote = true }: { settingsNote?: boolean }) {
  const state = useTradeRunState()
  const [confirming, setConfirming] = useState(false)
  if (!state) {
    return (
      <Button type="button" size="sm" disabled className="h-7 w-full gap-1.5 text-xs font-medium">
        <Play className="h-3.5 w-3.5" />
        Run
      </Button>
    )
  }

  const { blockers, isSubmitting, settings } = state
  const blocked = blockers.length > 0
  const sizing = settings.sizingMode === 'unit' ? 'Portfolio' : 'Engine cash'
  const note = blocked
    ? blockers[0]
    : settingsNote
      ? `${sizing} · ${formatSummaryNumber(settings.initialCash, CASH)}`
      : null

  return (
    <div>
      <div className="space-y-1.5">
        <Button
          type="button"
          size="sm"
          disabled={blocked || isSubmitting}
          title={blocked ? blockers[0] : undefined}
          onClick={(event) => {
            event.stopPropagation()
            setConfirming(true)
          }}
          className="h-7 w-full gap-1.5 text-xs font-medium"
          data-testid="trade-run-button"
        >
          <Play className="h-3.5 w-3.5" />
          {isSubmitting ? 'Starting run' : 'Run'}
        </Button>
        {note && (
          <div className="text-[11px] text-muted-foreground" data-testid="trade-run-note">
            {note}
          </div>
        )}
      </div>
      {/* Outside the spaced stack: the dialog leaves an empty element here. */}
      <Suspense fallback={null}>
        <RunConfirmDialog open={confirming} onOpenChange={setConfirming} state={state} />
      </Suspense>
    </div>
  )
}

/**
 * The bottom of the Run Settings node: Run, then the strategy's newest run,
 * its status while it runs and its result once done, one click from its
 * report. The run settings sit just above, so the button carries no settings
 * note.
 */
export function ReportRunSection() {
  const state = useTradeRunState()
  const run = state?.latestRun ?? null
  return (
    <div className="nodrag space-y-2" data-testid="report-run-section">
      <TradeRunButton settingsNote={false} />
      {state && run && (
        <LatestRunSummary run={run} onShow={state.showRun} onOpenReport={state.openReport} />
      )}
    </div>
  )
}

function LatestRunSummary({
  run,
  onShow,
  onOpenReport,
}: {
  run: ExecutionRecord
  onShow: (executionId: string) => void
  onOpenReport?: (executionId: string) => void
}) {
  const live = isLive(run.status)
  const progress = runProgress(run, run.completedResults, run.failedItems.length)
  const summary = summaryForRun(run)
  const pnl = summary?.totalPnl
  const age = relativeTime(run.triggeredAt, { addSuffix: true })
  const fact = 'flex items-baseline justify-between gap-2 text-[11px]'

  return (
    <div className="space-y-1 node-divider-t" data-testid="report-latest-run">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          onShow(run.executionId)
        }}
        className="w-full space-y-1 rounded text-left hover:bg-hover"
        title="Show this run in the Runs tab"
      >
        <span className={fact}>
          <span className="text-muted-foreground">Last run</span>
          <span className="truncate">
            {live
              ? `Running · ${progress.done} / ${progress.total}`
              : `${run.status} · ${age ?? ''}`}
          </span>
        </span>
        {!live && (
          <>
            <span className={fact}>
              <span className="text-muted-foreground">P&amp;L</span>
              <span
                className={cn(
                  'tabular-nums',
                  pnl == null ? '' : pnl >= 0 ? 'text-positive' : 'text-negative',
                )}
              >
                {formatSummaryNumber(pnl, USD)}
              </span>
            </span>
            <span className={fact}>
              <span className="text-muted-foreground">Trades · Win rate</span>
              <span className="tabular-nums">
                {formatSummaryNumber(summary?.totalTrades, COUNT)} ·{' '}
                {formatSummaryNumber(summary?.winRate, PERCENT)}
              </span>
            </span>
          </>
        )}
      </button>
      {onOpenReport && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={live}
          onClick={(event) => {
            event.stopPropagation()
            onOpenReport(run.executionId)
          }}
          className="h-7 w-full text-xs"
          data-testid="report-open-report"
        >
          {live ? 'Report opens when it finishes' : 'Open report'}
        </Button>
      )}
    </div>
  )
}

/**
 * The bottom of Simple Trade and Advanced Trade until they are converted to
 * the Trade Manager: Run, and the strategy's newest run, whose line opens the
 * Runs tab on it.
 */
export function TradeRunSection() {
  const state = useTradeRunState()
  return (
    <div className="nodrag space-y-1.5" data-testid="trade-run-section">
      <TradeRunButton />
      {state?.latestRun && <LatestRunLine run={state.latestRun} onShow={state.showRun} />}
    </div>
  )
}

function LatestRunLine({
  run,
  onShow,
}: {
  run: ExecutionRecord
  onShow: (executionId: string) => void
}) {
  const live = isLive(run.status)
  const progress = live ? runProgress(run, run.completedResults, run.failedItems.length) : null
  const pnl = run.executionSummary?.totalPnl
  const age = relativeTime(run.triggeredAt, { addSuffix: true })

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onShow(run.executionId)
      }}
      className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-[11px] text-muted-foreground hover:bg-hover hover:text-foreground"
      data-testid="trade-run-latest"
    >
      <span className="shrink-0 font-medium">{live ? 'Running' : 'Last run'}</span>
      <span className="truncate tabular-nums">
        {progress ? `${progress.done} / ${progress.total}` : (age ?? '')}
      </span>
      {!live && pnl != null && (
        <span
          className={cn(
            'ml-auto shrink-0 tabular-nums',
            pnl >= 0 ? 'text-positive' : 'text-negative',
          )}
        >
          {formatSummaryNumber(pnl, USD)}
        </span>
      )}
    </button>
  )
}
